<template>
  <LoadingSpinner v-if="meetingRequestSettingsState.loading" class="h-full grid place-items-center" />
  <div
    v-else-if="
      company &&
      meetingRequestSettingsState.meetingSettings &&
      (!meetingRequestSettingsState.meetingSettings.able_to_book || meetingRequestSettingsState.error)
    "
    class="flex flex-col w-full h-full bg-white"
  >
    <div class="container flex items-center justify-center w-full h-full">
      <div class="flex flex-col items-center w-full pb-4 gap-3 md_gap-6">
        <div class="mt-4">
          <img v-if="company && company.logo" class="object-contain w-full h-24 max-w-sm mb-4" :src="company.logo" />
          <h3 class="mb-12 text-xl font-medium text-center text-gray-900">
            <template v-if="company && company.name">Book a meeting with {{ company.name }}</template>
            <template v-else>Book a meeting with our calendar</template>
          </h3>
        </div>
        <div class="flex items-start max-w-full p-4 text-sm bg-blue-100 gap-4 rounded-md w-160" data-testid="book-a-meeting-disabled">
          <InformationCircleIconSolid class="w-5 mt-px text-blue-700" size="1.25x" />
          <div>
            <p class="mb-0 font-semibold">We are temporarily not accepting online bookings.</p>
            <nuxt-link
              v-if="isAskAQuestionEnabled"
              class="inline-block mt-4 mb-0 text-gray-900 cursor-pointer"
              data-testid="investor-flow-ask-a-question"
              font-weight="normal"
              :to="{ name: 'companies-id', params: { id: company.id }, query: { aaqOpen: 'true' } }"
            >
              Ask a Question
              <ArrowRightIcon class="w-5 mb-px ml-2 inline-block" size="1x" />
            </nuxt-link>
          </div>
        </div>
      </div>
      <div class="fixed flex items-center justify-center mb-6 bottom-12">
        <MarkLogo class="w-6 h-6" />
        <div class="h-6 ml-4 text-gray-900 leading-7">Powered by VRIFY</div>
      </div>
    </div>
  </div>
  <div v-else-if="company && meetingRequestSettingsState.meetingSettings" class="flex flex-col w-full h-full md_bg-white">
    <VAlert :is-visible="alerts.isAlertVisible" :banner-type="alerts.alertType" :timeout="alerts.alertTimeout" @clear-banner="hideAlert">
      <template #default>
        <p class="mb-0">{{ alerts.alertMessage }}</p>
      </template>
    </VAlert>
    <header class="mb-4 bg-white shadow md_shadow-none" :class="{ md_hidden: stepNumber === 1 }">
      <div class="container flex flex-col items-center">
        <div
          v-if="stepNumber === 2 || stepNumber === 3"
          class="flex flex-col w-full pb-4 mt-12 md_items-start md_gap-6 md_h-auto md_flex-row"
          :style="{ maxWidth: '850px' }"
        >
          <section class="md_flex md_gap-6">
            <div v-if="stepNumber > 1" class="flex items-center mb-4 text-gray-900 md_flex-col md_items-start">
              <div class="flex flex-grow">
                <InformationCircleIcon class="w-5 h-5 my-auto mr-4" />
                <span class="mr-2 text-xl">{{ meetingDuration }} Minute Meeting</span>
              </div>
              <VLink
                class="mr-1 w-min text-copper-700 md_ml-9"
                data-testid="change-meeting-duration"
                component="button"
                type="button"
                font-weight="normal"
                @click="stepNumber = 1"
              >
                change
              </VLink>
            </div>
            <div v-if="stepNumber > 2" class="flex items-center mb-4 text-gray-900 md_flex-col md_items-start">
              <div class="flex flex-grow">
                <CalendarIcon class="w-5 h-5 my-auto mr-4" />
                <span class="hidden mr-2 text-xl md_block">
                  {{ meetingDateTime && formatDateTimeDesktop(meetingDateTime) }}
                </span>
                <span class="mr-2 text-xl whitespace-pre-line md_hidden">
                  {{ meetingDateTime && formatDateTimeMobile(meetingDateTime) }}
                </span>
              </div>
              <VLink
                class="mr-1 w-min text-copper-700 md_ml-9"
                data-testid="change-meeting-date"
                component="button"
                type="button"
                font-weight="normal"
                @click="stepNumber = 2"
              >
                change
              </VLink>
            </div>
          </section>
        </div>
      </div>
    </header>
    <div v-if="stepNumber === 4" class="flex items-center justify-center h-full">
      <MeetingsSuccessView v-if="stepNumber === 4 && company" :company="company" />
    </div>
    <div v-else class="container flex justify-center flex-grow">
      <MeetingsChooseDuration
        v-if="stepNumber === 1"
        :company="company"
        :is-ask-a-question-enabled="isAskAQuestionEnabled"
        :meeting-settings="meetingRequestSettingsState.meetingSettings"
        @choose-duration=";(meetingDuration = $event), (stepNumber = 2)"
      />
      <MeetingsChooseTime
        v-else-if="stepNumber === 2"
        :company="company"
        :duration="meetingDuration!"
        :is-ask-a-question-enabled="isAskAQuestionEnabled"
        :token="meetingRequestSettingsState.token!"
        :subs="meetingRequestSettingsState.subs"
        @choose-time=";(meetingDateTime = $event), (stepNumber = 3)"
      />
      <MeetingsAboutYou
        v-else-if="stepNumber === 3 && meetingDateTime && meetingDuration"
        :user="userState.user"
        :company="company"
        :meeting-settings="meetingRequestSettingsState.meetingSettings"
        :meeting-date-time="meetingDateTime"
        :meeting-duration="meetingDuration"
        @back="stepNumber = 2"
        @submit="stepNumber = 4"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { DateTime } from 'luxon'
import { CalendarIcon, InformationCircleIcon, ArrowRightIcon } from '@heroicons/vue/24/outline'
import { InformationCircleIcon as InformationCircleIconSolid } from '@heroicons/vue/24/solid'
import MarkLogo from '~/assets/images/mark-logo.svg'

const props = defineProps({
  meetingSettingsId: {
    type: [Number, String] as PropType<number | string>,
    required: true,
  },
})

const { $sentry } = useNuxtApp()
const { alerts, hideAlert } = useAlerts()
const { state: userState } = useUser()
const { state: meetingRequestSettingsState } = useMeetingRequestSettings({ id: toRef(props, 'meetingSettingsId') })

const stepNumber = ref<number>(1)
const meetingDuration = ref<number | undefined>()
const meetingDateTime = ref<DateTime | undefined>()
const company = computed(() => meetingRequestSettingsState.value.company)
const isAskAQuestionEnabled = computed(() => company.value?.is_aaq_active)

watch(
  () => meetingRequestSettingsState.value.error,
  async e => {
    if (!e) return

    if ('statusCode' in e && e.statusCode === 404) {
      showError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        message: 'The page you are trying to access does not exist.',
      })
      return
    }

    if ('statusCode' in e && (e.statusCode === 401 || e.statusCode === 403)) {
      window.location.href = '/meetings'
    }
    $sentry?.captureException(e)
  },
)

onMounted(() => {
  window.addEventListener('beforeunload', confirmLeave)
})

onUnmounted(() => {
  window.removeEventListener('beforeunload', confirmLeave)
})

function confirmLeave() {
  stepNumber.value > 1 && confirm('Are you sure you want to leave this page?')
}

function formatDateTimeDesktop(dateTime: DateTime) {
  return dateTime.toLocaleString(DateTime.DATETIME_FULL)
}

function formatDateTimeMobile(dateTime: DateTime) {
  return dateTime.toLocaleString(DateTime.DATE_FULL) + '\n' + dateTime.toLocaleString(DateTime.TIME_SIMPLE)
}
</script>
